import { Button, Card, Col, DatePicker, Row, Select, Space, Table, Tag, Typography, Descriptions, Statistic, Badge, Modal, InputNumber, Input, Checkbox } from "antd";
import { WarningOutlined, CheckCircleOutlined, DollarOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";
import PageTitle from "../page-header/PageHeader";
import { generateSalarySheet, getWorkerLedger, settleWorkerSalary, unsettleWorkerSalary, getSavedSalarySheet } from "../../redux/actions/hr/salaryActions";
import { loadAllWorkers } from "../../redux/actions/hr/workerActions";
import SandwichFlagTable from "./SandwichFlagTable";

const { Title } = Typography;

const SalarySheetPage = () => {
  const dispatch = useDispatch();
  const { sheet, loading } = useSelector((s) => s.salary);
  const { list: workers } = useSelector((s) => s.workers);
  const [year, setYear] = useState(dayjs().year());
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);

  // Settlement state
  const [settleModalOpen, setSettleModalOpen] = useState(false);
  const [settleWorker, setSettleWorker] = useState(null);
  const [amountPaid, setAmountPaid] = useState(0);
  const [settleNote, setSettleNote] = useState("");
  const [settleLoading, setSettleLoading] = useState(false);
  const [savedRecords, setSavedRecords] = useState({}); // worker_id -> SalaryRecord

  useEffect(() => {
    dispatch(loadAllWorkers({ query: "all", status: "true" }));
  }, [dispatch]);

  // Load saved records when month/year changes
  const loadSaved = async () => {
    const records = await dispatch(getSavedSalarySheet(year, month));
    if (Array.isArray(records)) {
      const map = {};
      records.forEach((r) => { map[r.worker_id] = r; });
      setSavedRecords(map);
    }
  };

  useEffect(() => { loadSaved(); }, [year, month]);

  const handleGenerate = async () => {
    await dispatch(generateSalarySheet({ year, month }));
    await loadSaved();
  };

  const handleOpenSettle = (worker) => {
    setSettleWorker(worker);
    setAmountPaid(Math.max(0, worker.net_payable));
    setSettleNote("");
    setSettleModalOpen(true);
  };

  const handleSettle = async () => {
    if (!settleWorker) return;
    setSettleLoading(true);
    const result = await dispatch(settleWorkerSalary({
      worker_id: settleWorker.worker_id,
      year,
      month,
      amount_paid: amountPaid,
      note: settleNote,
    }));
    setSettleLoading(false);
    if (result) {
      setSettleModalOpen(false);
      await loadSaved();
    }
  };

  const handleUnsettle = async (workerId) => {
    Modal.confirm({
      title: "Reverse Settlement?",
      content: "This will undo the settlement so you can edit and re-settle. Carry-forward advances will be reversed.",
      okText: "Yes, Reverse",
      okType: "danger",
      onOk: async () => {
        const result = await dispatch(unsettleWorkerSalary({ worker_id: workerId, year, month }));
        if (result) {
          // Re-generate to reflect restored advances, then reload saved
          await dispatch(generateSalarySheet({ year, month }));
          await loadSaved();
        }
      },
    });
  };

  const handleViewLedger = async (workerId) => {
    setSelectedWorker(workerId);
    setLedgerLoading(true);
    const from = `${year}-${String(month).padStart(2, "0")}-01`;
    const lastDay = new Date(year, month, 0).getDate();
    const to = `${year}-${String(month).padStart(2, "0")}-${lastDay}`;
    const data = await dispatch(getWorkerLedger(workerId, from, to));
    setLedger(data);
    setLedgerLoading(false);
  };

  // Merge: for settled workers, use saved record data so Generate doesn't overwrite their numbers
  const displayWorkers = (sheet?.workers || []).map((w) => {
    const saved = savedRecords[w.worker_id];
    if (saved?.is_settled) {
      return {
        ...w,
        present_days: saved.present_days ?? w.present_days,
        absent_days: saved.absent_days ?? w.absent_days,
        total_hours: saved.total_hours ?? w.total_hours,
        overtime_hours: saved.overtime_hours ?? w.overtime_hours,
        gross_salary: saved.gross_salary ?? w.gross_salary,
        overtime_amount: saved.overtime_amount ?? w.overtime_amount,
        total_earnings: saved.total_earnings ?? w.total_earnings,
        total_advance: saved.total_advance ?? w.total_advance,
        net_payable: saved.net_payable ?? w.net_payable,
        sandwich_deduction: saved.sandwich_deduction ?? w.sandwich_deduction,
      };
    }
    return w;
  });

  const columns = [
    {
      title: "", key: "settled_check", width: 40, fixed: "left", align: "center",
      render: (_, r) => {
        const saved = savedRecords[r.worker_id];
        if (saved?.is_settled) return <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 16 }} />;
        return <span style={{ color: "#d9d9d9", fontSize: 16 }}>○</span>;
      },
    },
    { title: "Name", dataIndex: "name", key: "name", fixed: "left", width: 150 },
    { title: "Dept", dataIndex: "department", key: "department", width: 100 },
    { title: "Type", dataIndex: "salary_type", key: "salary_type", width: 80, render: (v) => <Tag color={v === "monthly" ? "blue" : "green"}>{v}</Tag> },
    { title: "Salary", key: "salary_rate", width: 100, align: "right", render: (_, r) => r.salary_type === "monthly" ? `₹${(r.monthly_salary || 0).toLocaleString()}` : `₹${r.hourly_rate || 0}/hr` },
    { title: "Present", dataIndex: "present_days", key: "present_days", width: 70, align: "center" },
    { title: "Absent", dataIndex: "absent_days", key: "absent_days", width: 70, align: "center" },
    { title: "Hours", dataIndex: "total_hours", key: "total_hours", width: 70, align: "center" },
    { title: "OT Hrs", dataIndex: "overtime_hours", key: "overtime_hours", width: 70, align: "center", render: (v) => v > 0 ? <Tag color="volcano">{v}</Tag> : 0 },
    {
      title: "Sandwich", key: "sandwich", width: 100, align: "center",
      render: (_, r) => {
        if (!r.sandwich_flags?.length) return "-";
        const pending = r.sandwich_pending || 0;
        const approved = r.sandwich_flags.filter((f) => f.status === "approved").length;
        return (
          <Space size={2}>
            {pending > 0 && <Badge count={pending} style={{ backgroundColor: "#faad14" }} title="Pending review" />}
            {approved > 0 && <Badge count={`-${r.sandwich_deduction}d`} style={{ backgroundColor: "#ff4d4f" }} title="Days deducted" />}
          </Space>
        );
      },
    },
    { title: "Gross", dataIndex: "gross_salary", key: "gross_salary", width: 100, align: "right", render: (v) => `₹${v.toLocaleString()}` },
    { title: "OT Amt", dataIndex: "overtime_amount", key: "overtime_amount", width: 90, align: "right", render: (v) => `₹${v.toLocaleString()}` },
    { title: "Total Earn", dataIndex: "total_earnings", key: "total_earnings", width: 100, align: "right", render: (v) => `₹${v.toLocaleString()}` },
    { title: "Advance", dataIndex: "total_advance", key: "total_advance", width: 90, align: "right", render: (v) => v > 0 ? <Tag color="red">₹{v.toLocaleString()}</Tag> : "₹0" },
    {
      title: "Net Pay", dataIndex: "net_payable", key: "net_payable", width: 110, align: "right",
      render: (v) => <Typography.Text strong style={{ color: v < 0 ? "red" : "green" }}>₹{v.toLocaleString()}</Typography.Text>,
    },
    {
      title: "Amt Paid", key: "amount_paid", width: 100, align: "right",
      render: (_, r) => {
        const saved = savedRecords[r.worker_id];
        if (!saved?.is_settled) return <span style={{ color: "#bbb" }}>-</span>;
        return <Typography.Text strong>₹{saved.amount_paid?.toLocaleString()}</Typography.Text>;
      },
    },
    {
      title: "Closing Bal", key: "closing_balance", width: 110, align: "right",
      render: (_, r) => {
        const saved = savedRecords[r.worker_id];
        if (!saved?.is_settled) return <span style={{ color: "#bbb" }}>-</span>;
        const closing = +(r.total_earnings - r.total_advance - (saved.amount_paid || 0)).toFixed(2);
        return (
          <Typography.Text strong style={{ color: closing < 0 ? "red" : closing > 0 ? "orange" : "green" }}>
            ₹{closing.toLocaleString()}
          </Typography.Text>
        );
      },
    },
    {
      title: "Settle", key: "settle", width: 140, fixed: "right", align: "center",
      render: (_, r) => {
        const saved = savedRecords[r.worker_id];
        if (saved?.is_settled) {
          return (
            <Space size={4}>
              <Tag color="green" style={{ cursor: "default", margin: 0 }}>
                ₹{saved.amount_paid?.toLocaleString()} ✓
              </Tag>
              <Button
                size="small"
                type="link"
                danger
                onClick={() => handleUnsettle(r.worker_id)}
                style={{ padding: "0 4px", fontSize: 11 }}
              >
                Edit
              </Button>
            </Space>
          );
        }
        return (
          <Button
            size="small"
            type="primary"
            icon={<DollarOutlined />}
            onClick={() => handleOpenSettle(r)}
            disabled={!savedRecords[r.worker_id] && !sheet?.workers?.length}
          >
            Settle
          </Button>
        );
      },
    },
    {
      title: "", key: "ledger", width: 60, fixed: "right",
      render: (_, r) => <Button size="small" type="link" onClick={() => handleViewLedger(r.worker_id)}>Ledger</Button>,
    },
  ];

  const months = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: dayjs().month(i).format("MMMM") }));

  return (
    <>
      <PageTitle title="Back" />
      <Card title="Salary Sheet">
        <Space style={{ marginBottom: 16 }} wrap>
          <Select value={month} onChange={setMonth} style={{ width: 130 }}>
            {months.map((m) => <Select.Option key={m.value} value={m.value}>{m.label}</Select.Option>)}
          </Select>
          <Select value={year} onChange={setYear} style={{ width: 100 }}>
            {[2024, 2025, 2026, 2027].map((y) => <Select.Option key={y} value={y}>{y}</Select.Option>)}
          </Select>
          <Button type="primary" onClick={handleGenerate} loading={loading}>Generate Salary Sheet</Button>
        </Space>

        {sheet && (
          <>
            <Row gutter={16} style={{ marginBottom: 16 }}>
              <Col><Statistic title="Period" value={`${sheet.period.from} → ${sheet.period.to}`} valueStyle={{ fontSize: 14 }} /></Col>
              <Col><Statistic title="Days in Month" value={sheet.period.month_days} /></Col>
              <Col><Statistic title="Total Gross" value={`₹${sheet.totals.total_gross.toLocaleString()}`} /></Col>
              <Col><Statistic title="Total OT" value={`₹${sheet.totals.total_overtime.toLocaleString()}`} /></Col>
              <Col><Statistic title="Total Advance" value={`₹${sheet.totals.total_advance.toLocaleString()}`} valueStyle={{ color: "red" }} /></Col>
              <Col><Statistic title="Net Payable" value={`₹${sheet.totals.total_payable.toLocaleString()}`} valueStyle={{ color: "green" }} /></Col>
              {sheet.totals.total_sandwich_pending > 0 && (
                <Col>
                  <Statistic
                    title="Sandwich Pending"
                    value={sheet.totals.total_sandwich_pending}
                    valueStyle={{ color: "#faad14" }}
                    prefix={<WarningOutlined />}
                  />
                </Col>
              )}
            </Row>
            <Table
              dataSource={displayWorkers}
              columns={columns}
              rowKey="worker_id"
              size="small"
              pagination={false}
              scroll={{ x: 1600 }}
              bordered
              rowClassName={(r) => r.sandwich_pending > 0 ? "ant-table-row-warning" : ""}
              expandable={{
                expandedRowRender: (r) => r.sandwich_flags?.length > 0 ? (
                  <SandwichFlagTable
                    flags={r.sandwich_flags}
                    onDecisionMade={handleGenerate}
                  />
                ) : null,
                rowExpandable: (r) => r.sandwich_flags?.length > 0,
              }}
            />
          </>
        )}
      </Card>

      {/* ── Worker Ledger ─────────────────────────────────────────── */}
      {ledger && (
        <Card title={`Ledger: ${ledger.worker.name} (${ledger.period.from} → ${ledger.period.to})`} style={{ marginTop: 16 }} loading={ledgerLoading}>
          <Descriptions bordered size="small" column={{ xs: 1, md: 3 }}>
            <Descriptions.Item label="Salary Type"><Tag>{ledger.worker.salary_type}</Tag></Descriptions.Item>
            <Descriptions.Item label="Rate">
              {ledger.worker.salary_type === "monthly" ? `₹${ledger.worker.monthly_salary?.toLocaleString()}/mo` : `₹${ledger.worker.hourly_rate}/hr`}
            </Descriptions.Item>
            <Descriptions.Item label="Shift Hours">{ledger.worker.shift_hours} hrs</Descriptions.Item>
            <Descriptions.Item label="Total Advance" span={3}>
              <Tag color="red">₹{ledger.total_advance.toLocaleString()}</Tag>
            </Descriptions.Item>
          </Descriptions>

          <Title level={5} style={{ marginTop: 16 }}>Attendance</Title>
          <Table
            dataSource={ledger.attendance}
            rowKey="id"
            size="small"
            pagination={false}
            columns={[
              { title: "Date", dataIndex: "date", render: (v) => dayjs(v).format("DD-MMM (ddd)") },
              { title: "Status", dataIndex: "status", render: (v) => <Tag color={v === "present" ? "green" : v === "absent" ? "red" : "orange"}>{v}</Tag> },
              { title: "In", dataIndex: "in_time", render: (v) => v ? dayjs(v).format("hh:mm A") : "-" },
              { title: "Out", dataIndex: "out_time", render: (v) => v ? dayjs(v).format("hh:mm A") : "-" },
              { title: "Hrs", dataIndex: "total_hours" },
              { title: "OT", dataIndex: "overtime_hours" },
              { title: "Note", dataIndex: "note" },
            ]}
            rowClassName={(r) => {
              // Highlight sandwich-affected dates
              if (!ledger.sandwich_flags?.length) return "";
              const dateStr = dayjs(r.date).format("YYYY-MM-DD");
              const isSandwich = ledger.sandwich_flags.some((f) =>
                f.affected_dates?.some((d) => dayjs(d).format("YYYY-MM-DD") === dateStr)
              );
              return isSandwich ? "ant-table-row-warning" : "";
            }}
          />

          {ledger.sandwich_flags?.length > 0 && (
            <>
              <Title level={5} style={{ marginTop: 16 }}>
                <WarningOutlined style={{ color: "#faad14", marginRight: 8 }} />
                Sandwich Leave Flags
              </Title>
              <SandwichFlagTable
                flags={ledger.sandwich_flags}
                onDecisionMade={() => handleViewLedger(selectedWorker)}
              />
            </>
          )}

          {ledger.advances.length > 0 && (
            <>
              <Title level={5} style={{ marginTop: 16 }}>Advances</Title>
              <Table
                dataSource={ledger.advances}
                rowKey="id"
                size="small"
                pagination={false}
                columns={[
                  { title: "Date", dataIndex: "date", render: (v) => dayjs(v).format("DD-MMM-YYYY") },
                  { title: "Amount", dataIndex: "amount", render: (v) => `₹${v.toLocaleString()}` },
                  { title: "Status", dataIndex: "status", render: (v) => <Tag>{v}</Tag> },
                  { title: "Note", dataIndex: "note" },
                ]}
              />
            </>
          )}
        </Card>
      )}

      {/* ── Settle Modal ─────────────────────────────────────────── */}
      <Modal
        title={`Settle Salary: ${settleWorker?.name}`}
        open={settleModalOpen}
        onCancel={() => setSettleModalOpen(false)}
        onOk={handleSettle}
        confirmLoading={settleLoading}
        okText="Settle & Close"
        okButtonProps={{ danger: amountPaid < (settleWorker?.net_payable || 0) }}
      >
        {settleWorker && (
          <div>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Total Earnings">₹{settleWorker.total_earnings?.toLocaleString()}</Descriptions.Item>
              <Descriptions.Item label="Advance Deducted">
                <Typography.Text type="danger">₹{settleWorker.total_advance?.toLocaleString()}</Typography.Text>
              </Descriptions.Item>
              <Descriptions.Item label="Net Payable" span={2}>
                <Typography.Text strong style={{ fontSize: 18, color: settleWorker.net_payable >= 0 ? "green" : "red" }}>
                  ₹{settleWorker.net_payable?.toLocaleString()}
                </Typography.Text>
              </Descriptions.Item>
            </Descriptions>

            <div style={{ marginBottom: 12 }}>
              <Typography.Text strong>Amount Paid:</Typography.Text>
              <InputNumber
                value={amountPaid}
                onChange={setAmountPaid}
                style={{ width: "100%", marginTop: 4 }}
                size="large"
                
                prefix="₹"
                formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                parser={(v) => v.replace(/₹\s?|(,*)/g, "")}
              />
            </div>

            {amountPaid > settleWorker.net_payable && (
              <Tag color="blue" style={{ marginBottom: 8 }}>
                Overpayment: ₹{(amountPaid - settleWorker.net_payable).toLocaleString()} → will be carried as advance for next month
              </Tag>
            )}
            {amountPaid < settleWorker.net_payable && (
              <Tag color="orange" style={{ marginBottom: 8 }}>
                Underpayment: ₹{(settleWorker.net_payable - amountPaid).toLocaleString()} → owed to worker, reduces future advance
              </Tag>
            )}
            {amountPaid === settleWorker.net_payable && (
              <Tag color="green" style={{ marginBottom: 8 }}>Exact amount — no carry-forward</Tag>
            )}

            <Input.TextArea
              placeholder="Settlement note (optional)"
              value={settleNote}
              onChange={(e) => setSettleNote(e.target.value)}
              rows={2}
              style={{ marginTop: 8 }}
            />
          </div>
        )}
      </Modal>
    </>
  );
};

export default SalarySheetPage;
